import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, map, mergeMap, of, takeUntil } from 'rxjs';
import { clearAllState } from '../../../../core/store/actions/app.actions';
import { patientSwitchInitiated } from '../../../../user/store/actions/user.actions';
import { selectPatientId } from '../../../../user/store/selectors/user.selectors';
import { ScreeningsService } from '../../services/screenings/screenings.service';
import {
  getQuestionnaireDetail,
  getQuestionnaireDetailFailure,
  getQuestionnaireDetailSuccess,
  getQuestionnaireHistory,
  getQuestionnaireHistoryFailure,
  getQuestionnaireHistorySuccess,
  getReminderDetail,
  getReminderDetailFailure,
  getReminderDetailSuccess,
  getScreeningDetail,
  getScreeningDetailFailure,
  getScreeningDetailSuccess,
  getScreeningHistory,
  getScreeningHistoryFailure,
  getScreeningHistorySuccess,
  getScreeningsSummary,
  getScreeningsSummaryFailure,
  getScreeningsSummarySuccess,
} from '../actions/screenings.actions';

@Injectable()
export class ScreeningsEffects {
  private actions$ = inject(Actions);
  private screeningsService = inject(ScreeningsService);
  private store = inject(Store);

  // In-flight requests are abandoned when the patient switches or the account is cleared, so a late
  // response never lands in the new patient's slice (mirrors immunizations/allergies).
  private readonly cancelRequest$ = this.actions$.pipe(ofType(patientSwitchInitiated, clearAllState));

  getScreeningsSummary$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(getScreeningsSummary),
      concatLatestFrom(() => [this.store.select(selectPatientId)]),
      mergeMap(([, patientId]) =>
        this.screeningsService.getSummary(patientId).pipe(
          takeUntil(this.cancelRequest$),
          map(summary => getScreeningsSummarySuccess({ summary })),
          catchError(err => of(getScreeningsSummaryFailure({ error: err })))
        )
      )
    );
  });

  getQuestionnaireHistory$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(getQuestionnaireHistory),
      concatLatestFrom(() => [this.store.select(selectPatientId)]),
      mergeMap(([{ templateKey }, patientId]) =>
        this.screeningsService.getQuestionnaireHistory(patientId, templateKey).pipe(
          takeUntil(this.cancelRequest$),
          map(entries => getQuestionnaireHistorySuccess({ entries })),
          catchError(err => of(getQuestionnaireHistoryFailure({ error: err })))
        )
      )
    );
  });

  getQuestionnaireDetail$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(getQuestionnaireDetail),
      concatLatestFrom(() => [this.store.select(selectPatientId)]),
      mergeMap(([{ patientQuestionnaireId }, patientId]) =>
        this.screeningsService.getQuestionnaireDetail(patientId, patientQuestionnaireId).pipe(
          takeUntil(this.cancelRequest$),
          map(detail => getQuestionnaireDetailSuccess({ detail })),
          catchError(err => of(getQuestionnaireDetailFailure({ error: err })))
        )
      )
    );
  });

  getScreeningHistory$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(getScreeningHistory),
      concatLatestFrom(() => [this.store.select(selectPatientId)]),
      mergeMap(([{ conceptKey }, patientId]) =>
        this.screeningsService.getScreeningHistory(patientId, conceptKey).pipe(
          takeUntil(this.cancelRequest$),
          map(entries => getScreeningHistorySuccess({ entries })),
          catchError(err => of(getScreeningHistoryFailure({ error: err })))
        )
      )
    );
  });

  getScreeningDetail$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(getScreeningDetail),
      concatLatestFrom(() => [this.store.select(selectPatientId)]),
      mergeMap(([{ patientScreeningId }, patientId]) =>
        this.screeningsService.getScreeningDetail(patientId, patientScreeningId).pipe(
          takeUntil(this.cancelRequest$),
          map(detail => getScreeningDetailSuccess({ detail })),
          catchError(err => of(getScreeningDetailFailure({ error: err })))
        )
      )
    );
  });

  getReminderDetail$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(getReminderDetail),
      concatLatestFrom(() => [this.store.select(selectPatientId)]),
      mergeMap(([{ patientStudyReminderId }, patientId]) =>
        this.screeningsService.getReminderDetail(patientId, patientStudyReminderId).pipe(
          takeUntil(this.cancelRequest$),
          map(detail => getReminderDetailSuccess({ detail })),
          catchError(err => of(getReminderDetailFailure({ error: err })))
        )
      )
    );
  });
}
